(ns guidebook.views.cash-flow
  (:import
   [goog.i18n NumberFormat])
  (:require
   [guidebook.common :as gc]
   [very.re-frame.core :as rf]
   [goog.i18n.NumberFormat]
   [cljss.core :as cljss]
   [css.vars :as vars]
   [css.typography :as typo]
   [main.common :as common]
   [css.core
    :refer [get-theme]
    :refer-macros [defstyled]]))

(defstyled cash-flow-sheet-s :div
  {:min-height "100vh"
   :padding "135px 100px"
   :display "grid"
   :grid-gap "4rem 6rem"
   :grid-template-columns "1fr 1fr"
   :position "relative"})

(def formatter (NumberFormat. (.-CURRENCY (.-Format NumberFormat))))

(defn income-row [type value]
  (let [label (condp = type
                :salary "Salary + income"
                :investment-income "Income from investment"
                :social-security-income "Social security"
                :other-income "Other Income")]
    [gc/make-row {:label label
                  :value (.format formatter value)}]))

(defn expense-row [type value]
  (let [label (condp = type
                :monthly-mortgage-expense "Mortgage + property tax"
                :recurring-expenses "Recurring expenses (utilities, groceries, etc.)"
                :monthly-education-expense "Tuition"
                :entertainment-expense "Entertainment"
                :recurring-payments "Recurring payments (car loans, etc.)")]
    [gc/make-row {:label label
                  :value (.format formatter value)}]))

(defn avm [{:keys [value annual? income?]}]
  "Annual vs monthly. Income values need to be divided by 12 for monthly and expense values need to be multiplied by 12."
  (if income?
    (if annual? value (/ value 12))
    (if annual? (* value 12) value)))

(defn income-table [{:keys [salary other-income investment-income social-security-income total-income]} annual?]
  [gc/table-s {:margin-top "4rem"}
   [gc/header-row-s
    [gc/header-s "Cash Inflow"]
    [gc/header-label-s (if annual? "Annually" "Monthly")]]
   (when salary
     [income-row :salary (avm {:value salary
                               :annual? annual?
                               :income? true})])
   (when investment-income
     [income-row :investment-income (avm {:value investment-income
                                          :annual? annual?
                                          :income? true})])
   (when social-security-income
     [income-row :social-security-income (avm {:value social-security-income
                                               :annual? annual?
                                               :income? true})])
   (when other-income
     [income-row :other-income (avm {:value other-income
                                     :annual? annual?
                                     :income? true})])
   [gc/total-row-s
    [gc/header-s "Total"]
    [gc/header-s
     (.format formatter (avm {:value total-income
                              :annual? annual?
                              :income? true}))]]])

(defn expense-table [{:keys [monthly-education-expense entertainment-expense recurring-expenses monthly-mortgage-expense recurring-payments total-expense]} annual?]
  [gc/table-s
   [gc/header-row-s
    [gc/header-s "Cash Outflow"]
    [gc/header-label-s (if annual? "Annually" "Monthly")]]
   (when monthly-mortgage-expense
     [expense-row :monthly-mortgage-expense (avm {:value monthly-mortgage-expense
                                                  :annual? annual?})])
   (when recurring-expenses
     [expense-row :recurring-expenses (avm {:value recurring-expenses
                                            :annual? annual?})])
   (when monthly-education-expense
     [expense-row :monthly-education-expense (avm {:value monthly-education-expense
                                                   :annual? annual?})])
   (when entertainment-expense
     [expense-row :entertainment-expense (avm {:value entertainment-expense
                                               :annual? annual?})])
   (when recurring-payments
     [expense-row :recurring-payments (avm {:value recurring-payments
                                            :annual? annual?})])
   [gc/total-row-s
    [gc/header-s "Total"]
    [gc/header-s
     (.format formatter (avm {:value total-expense
                              :annual? annual?}))]]])

(defn totals-table [{:keys [total-income total-expense]} annual?]
  (let [corrected-income (avm {:value total-income
                               :annual? annual?
                               :income? true})
        corrected-expenses (avm {:value total-expense
                                 :annual? annual?})
        net-worth (- corrected-income corrected-expenses)]
    [gc/table-s
     [gc/make-row {:label "Total Inflow"
                   :value (.format formatter corrected-income)
                   :color vars/green}]
     [gc/make-row {:label "Total Outflow"
                   :value (str "-" (.format formatter corrected-expenses))
                   :color vars/red}]
     [gc/net-worth-row-s
      [gc/net-worth-header-s {:data-test "guidebook-cash-flow-total-header"} (if annual? "Annual Cash Flow" "Monthly Cash Flow")]
      [gc/net-worth-header-s {:color (if (< net-worth 0) vars/red vars/green)} (.format formatter net-worth)]]]))

(defn annual [data]
  [:div
   [income-table data true]
   [expense-table data true]
   [totals-table data true]])

(defn monthly [data]
  [:div
   [income-table data]
   [expense-table data]
   [totals-table data]])

(defn main []
  (let [data (rf/subscribe [:guidebook/balance-sheet])]
    (fn []
      [cash-flow-sheet-s
       [common/grid-s {:grid-template-columns ".5fr"
                       :grid-column "span 2"}
        [gc/title-s "Simple Cash Flow"]
        [gc/body-s "Your simple 5 year cashflow paints a picture of how we can deploy your income and assets over the next 5 years towards your goals."]]
       [annual @data]
       [monthly @data]])))
